import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { useEffect, useMemo, useReducer, useState } from "react";

import { toast } from "react-hot-toast";

import { Loader } from "../../../MainComponents";
import HelmetTags from "../../../MainComponents/HelmetTags";
import useAxios from "../../../MainComponents/Hooks/useAxios";

import Question from "./component/displayQuestion/Question";
import QuizFooter from "./component/quizFooter/QuizFooter";
import QuizHeader from "./component/quizHeader/QuizHeader";
import RightQuestionsSidebar from "./component/quizHeader/RightQuestionsSidebar";

import DisplayAnswers from "../QuizNew/component/displayAnswers/DisplayAnswers";
import PercentageSidebar from "../QuizNew/component/displayAnswers/PercentageSidebar";
import QuestionsAnswers from "../QuizNew/component/displayAnswers/QuestionsAnswers";
import { DisplayQuestion } from "../QuizNew/component/displayQuestion/DisplayQuestion";
import "./style/ExamSystem.scss";
import { useNavigate, useParams } from "react-router-dom";
import { Splitter } from "antd";
import { isMobile } from "react-device-detect";

function reducer(state, action) {
  switch (action.type) {
    case "setLoading": {
      return {
        ...state,
        loading: action.payload,
      };
    }
    case "setCollapse": {
      return {
        ...state,
        collapse: !state.collapse,
      };
    }

    case "setQuestions": {
      return {
        ...state,
        questions: action.payload.map((q) => ({
          ...q,
          flagged: q.flagged || false,
          order: q.selected_answer,
          n: q.n,
        })),
      };
    }
    case "setParagraphs": {
      return {
        ...state,
        paragraphs: action.payload,
      };
    }

    case "setQuizInfo": {
      return {
        ...state,
        quizInfo: action.payload,
      };
    }

    case "setCurrentQuestionIndex":
      const targetIndex = state.questions.findIndex(
        (q) => q.id === action.payload
      );
      return {
        ...state,
        currentQuestionIndex:
          targetIndex !== -1 ? targetIndex : state.currentQuestionIndex,
      };
    case "setCurrentSectionIndex":
      return {
        ...state,
        currentSectionIndex: action.payload,
      };

    case "setIsShowAnswers": {
      return {
        ...state,
        isShowAnswers: true,
      };
    }

    case "setAnswer": {
      return {
        ...state,
        questions: state.questions.map((q) =>
          q.id === action.payload.id
            ? {
                ...q,
                order: action.payload.order,
                n: action.payload.n,
              }
            : q
        ),
      };
    }

    case "toggleFlagQuestion": {
      const updatedQuestions = state.questions?.map((question) =>
        question.id === action.payload
          ? { ...question, flagged: !question.flagged }
          : question
      );

      const flags = updatedQuestions.filter((q) => q.flagged)?.map((q) => q.id);
      localStorage.setItem("flaggedQuestions", JSON.stringify(flags));

      return {
        ...state,
        questions: updatedQuestions,
      };
    }

    case "setFinishQuizData": {
      return {
        ...state,
        finishQuizData: action.payload,
      };
    }

    case "setAllData": {
      return {
        ...initialState,
      };
    }
    case "setRefetch": {
      return {
        ...state,
        refetch: action.payload,
      };
    }

    case "setSubmitExam": {
      return {
        ...state,
        submitExam: action.payload,
      };
    }

    default:
      throw Error("Unknown action: " + action.type);
  }
}

const initialState = {
  loading: true,
  currentSectionIndex: 0,
  currentQuestionIndex: 0,
  collapse: false,
  questions: [],
  paragraphs: [],
  quizInfo: {},
  answeredQuestions: [],
  finishQuizData: {},
  numOfSelected: 0,
  isShowAnswers: false,
  refetch: false,
  submitExam: {
    flag: "",
    dependency: false,
    data: {},
  },
};
const ExamSystem = ({ exam }) => {
  const { ID } = useParams();

  const navigate = useNavigate();

  const [show, setShow] = useState(false);

  const [state, dispatch] = useReducer(reducer, initialState);

  const [quizData, quizErrors, quizLoading] = useAxios(
    `${process.env.REACT_APP_STUDENT_START_MATRIALE_EXAM_PRACTISE_API}/${ID}`,
    "GET",
    "GET",
    state.refetch,
    ""
  );

  const [finishQuiz, finishErrors, finishLoading] = useAxios(
    process.env.REACT_APP_STUDENT_TRIAL_EXAM_SUBMIT_API,
    "POST",
    state.submitExam.flag,
    state.submitExam.dependency,
    state.submitExam.data,
    true
  );

  const currentNSection =
    state.questions[state.currentQuestionIndex]?.n_section;

  const currentParagraph = state.paragraphs.find(
    (p) => p.n_section === currentNSection
  );

  // 1. Get all n_section values from questions
  const sectionIds = state.questions.map((q) => q.n_section);

  // 2. Check if all n_section values are null
  const isSectionMode = !sectionIds.every((id) => id == null);

  const uniqueSections = isSectionMode
    ? [...new Set(sectionIds)].sort((a, b) => a - b)
    : state.questions.map((q) => q.id);

  useEffect(() => {
    const savedFlags = JSON.parse(
      localStorage.getItem("flaggedQuestions") || "[]"
    );

    const questionsWithFlags = quizData
      ? [...quizData?.choosequestions, ...quizData?.essayquestions].map(
          (q) => ({
            ...q,
            flagged: savedFlags.includes(q.id),
          })
        )
      : [];

    dispatch({
      type: "setQuestions",
      payload: questionsWithFlags,
    });

    dispatch({
      type: "setQuizInfo",
      payload: quizData ? quizData.quiz : {},
    });

    dispatch({
      type: "setParagraphs",
      payload: quizData ? quizData.paragraph : [],
    });

    dispatch({ type: "setRefetch", payload: false });
  }, [quizData]);

  useMemo(() => {
    // set axios
    axios.defaults.headers.common["Authorization"] =
      "Bearer " + JSON.parse(localStorage.userData).student_token;
  }, []);

  const onFinishQuiz = () => {
    const answers = state.questions
      .filter((q) => q.order)
      .map((q) => ({
        id: q.id,
        order: q.order,
        n: q.n,
      }));

    dispatch({
      type: "setSubmitExam",
      payload: {
        flag: "finish",
        dependency: !state.submitExam.dependency,
        data: {
          id: ID,
          answers,
        },
      },
    });
  };

  useEffect(() => {
    if (finishQuiz) {
      localStorage.removeItem("flaggedQuestions");

      dispatch({ type: "setFinishQuizData", payload: finishQuiz.data });
      navigate(-1);
    }
  }, [finishQuiz]);

  useEffect(() => {
    if (finishErrors) {
      dispatch({
        type: "setSubmitExam",
        payload: {
          flag: "",
          dependency: false,
          data: {},
        },
      });
    }
  }, [finishErrors]);

  return (
    <section
      className={`flex !z-[999999]   w-full flex-col items-center bg-white ${
        state.isShowAnswers ? "pt-[80px]" : "pt-[80px]"
      }`}
    >
      <HelmetTags title={`Exam | Nash Academy `}></HelmetTags>
      {quizErrors && (
        <div className="flex justify-center items-center h-screen w-full flex-col">
          <p className="text-5xl font-bold">
            {quizErrors?.response?.data?.message}
          </p>
          <div
            onClick={() => navigate(-1)}
            className="flex justify-center my-6"
          >
            <div className="collapse-btn top-[96px] flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-accent text-light shadow-md shadow-black/40 duration-300 hover:scale-110 hover:text-light">
              <FontAwesomeIcon
                className={`transition-all duration-300 ease-in-out hover:scale-110 ${
                  state.collapse && "rotate-180"
                }`}
                icon={faChevronRight}
              />
            </div>
          </div>
        </div>
      )}{" "}
      {quizLoading && <Loader />}
      {!quizLoading && !quizErrors && (
        <main className="w-full flex-col  flex  justify-center items-center">
          {!state.isShowAnswers ? (
            <>
              <QuizHeader
                state={state}
                dispatch={dispatch}
                onFinishQuiz={onFinishQuiz}
                quizErrors={quizErrors}
                quizLoading={quizLoading}
                finishLoading={finishLoading}
              />

              <DisplayQuestion state={state}>
                {state?.paragraphs.length > 0 ? (
                  <Splitter
                    layout={isMobile ? "vertical" : "horizontal"}
                    style={{ height: "80vh" }}
                  >
                    <Splitter.Panel
                      defaultSize="40%"
                      min="20%"
                      max="80%"
                      splitBarDraggableSize={40}
                      splitBarSize={4}
                    >
                      <div className="p-4 overflow-auto h-full">
                        <p
                          dangerouslySetInnerHTML={{
                            __html: currentParagraph?.paragraph,
                          }}
                        />
                      </div>
                    </Splitter.Panel>
                    <Splitter.Panel>
                      <Question
                        state={state}
                        dispatch={dispatch}
                        exam={exam}
                        isParagraphe
                      />
                    </Splitter.Panel>
                  </Splitter>
                ) : (
                  <>
                    <Question state={state} dispatch={dispatch} exam={exam} />
                  </>
                )}
              </DisplayQuestion>

              <QuizFooter
                state={state}
                dispatch={dispatch}
                onFinishQuiz={onFinishQuiz}
                isParagraphe={state?.paragraphs.length > 0}
                quizData={quizData}
                setShow={setShow}
                uniqueSections={uniqueSections}
                isSectionMode={isSectionMode}
              />
            </>
          ) : (
            <DisplayAnswers>
              <QuestionsAnswers state={state} dispatch={dispatch} />
              <PercentageSidebar state={state} />
            </DisplayAnswers>
          )}

          <RightQuestionsSidebar
            setShow={setShow}
            show={show}
            state={state}
            dispatch={dispatch}
          />
        </main>
      )}
    </section>
  );
};

export default ExamSystem;
